import colors from '@Common/assets/colors';
import Table from '@Common/Table';
import { useGeneratorContext } from '@Context/GeneratorContext';
import { JiraIssue } from '@Models/types';
import { MagnifyingGlass, SquaresFour, X } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { BounceLoader, MoonLoader } from 'react-spinners';

const JiraIssues = () => {
  const {
    jiraIssues,
    selectedJiraProject,
    fetchJiraIssues,
    setSelectedJiraIssue,
    fetchIssueDescrips, // <-- 1. Added the fetch function from context
    genPageLoading,
    setGenPageLoading,
  } = useGeneratorContext();

  const loading = genPageLoading;
  const [search, setSearch] = useState('');
  const [selectedRow, setSelectedRow] = useState<JiraIssue | null>(null);
  const [filteredSearch, setFilteredSearch] = useState<JiraIssue[]>(jiraIssues);

  useEffect(() => {
    setFilteredSearch(jiraIssues);
  }, [jiraIssues]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearch(query);
    setFilteredSearch(
      jiraIssues.filter(
        (issue: JiraIssue) =>
          issue.summary.toLowerCase().includes(query.toLowerCase()) ||
          issue.key.toLowerCase().includes(query.toLowerCase())
      )
    );
  };

  useEffect(() => {
    if (loading) {
      setSearch('');
    }
  }, [loading]);

  const clearSearch = () => {
    setSearch('');
    setFilteredSearch(jiraIssues);
  };

  useEffect(() => {
    if (selectedJiraProject) {
      setGenPageLoading(true);
      fetchJiraIssues(selectedJiraProject.key).finally(() =>
        setGenPageLoading(false)
      );
    }
  }, [selectedJiraProject, setGenPageLoading]);

  const columns: {
    header: string;
    accessor: keyof JiraIssue;
    Cell?: (
      value: JiraIssue[keyof JiraIssue],
      row: JiraIssue
    ) => React.ReactNode;
  }[] = [
    { header: 'Summary', accessor: 'summary' },
    {
      header: 'Key',
      accessor: 'key',
      Cell: (value: JiraIssue[keyof JiraIssue]) => (
        <span className="whitespace-nowrap font-bold">
          {typeof value === 'string' ? value : null}
        </span>
      ),
    },
  ];

  const handleSelectIssue = (issue: JiraIssue) => {
    setSelectedRow(issue);
    setSelectedJiraIssue(issue);

    // 2. Triggers the adapter mapped fetch for the Requirements panel
    fetchIssueDescrips(issue.key);
  };

  return (
    <div
      data-testid="test-jira-issues"
      className="flex h-full flex-col rounded-lg bg-white"
    >
      <span className="flex w-full flex-col items-start justify-between gap-y-2 rounded-t-md bg-primary-700 px-4 py-2 sm:flex-row">
        <span className="font-extrabold text-white">Issues</span>

        <div className="flex w-full flex-row items-center gap-x-2 rounded-md border bg-white px-2 py-1 sm:w-56">
          <MagnifyingGlass size={20} weight="bold" />
          <input
            disabled={loading}
            type="text"
            className={`w-full border-gray-200 font-inter text-sm focus:outline-none ${loading && 'animate-pulse'}`}
            placeholder={loading ? 'Loading Jira Issues' : 'Search Jira Issue'}
            value={search}
            onChange={handleSearch}
          />

          {loading ? (
            <BounceLoader
              data-testid="test-issue-search-loading"
              size={20}
              color={colors.brandWarning[500]}
            />
          ) : (
            <X
              data-testid="test-clear-btn"
              weight="bold"
              className="cursor-pointer hover:text-brandRed-500"
              onClick={clearSearch}
            />
          )}
        </div>
      </span>

      <div className="mx-2 mb-2 flex h-0 flex-grow flex-col">
        {/* NO PROJECT SELECTED */}
        {!selectedJiraProject && (
          <span className="flex h-full flex-col items-center justify-center text-neutral-300">
            <SquaresFour size={100} weight="bold" />
            <label className="text-xs text-neutral-500">
              Select a project first
            </label>
          </span>
        )}

        {/* NO ISSUES AVAILABLE */}
        {!loading && selectedJiraProject && jiraIssues.length === 0 && (
          <span className="flex h-full flex-col items-center justify-center text-neutral-300">
            <SquaresFour size={100} weight="bold" />
            <label className="text-xs text-neutral-500">
              No Issues Available
            </label>
          </span>
        )}

        {/* LOADING STATE */}
        {loading && selectedJiraProject ? (
          <span className="flex h-full w-full flex-col items-center justify-center sm:gap-y-6 sm:p-5">
            <MoonLoader color="#071D49" />
            <span className="animate-pulse text-xs font-semibold tracking-wide text-gray-600 sm:text-sm">
              Jira Issues Loading
            </span>
          </span>
        ) : (
          <>
            {/* ISSUES TABLE */}
            {filteredSearch && filteredSearch.length > 0 && (
              <Table
                columns={columns}
                data={filteredSearch}
                selectedRow={selectedRow}
                rowClickHandler={handleSelectIssue}
              />
            )}

            {/* NO SEARCH RESULTS */}
            {selectedJiraProject &&
              !loading &&
              jiraIssues.length > 0 &&
              filteredSearch.length === 0 && (
                <span className="flex h-full flex-col items-center justify-center text-neutral-300">
                  <SquaresFour size={100} weight="bold" />
                  <label className="text-xs text-neutral-500">
                    No Issues Found
                  </label>
                </span>
              )}
          </>
        )}
      </div>
    </div>
  );
};

export default JiraIssues;
